import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { EmptyState, InlineLoader } from "@/components/shared";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { MessageSquare, ArrowDownLeft, ArrowUpRight, Inbox, Ban, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-ff-border bg-white p-4" data-testid={testid}>
      <div className="text-2xl font-extrabold text-ff-blue">{value}</div>
      <div className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-ff-medium">{label}</div>
    </div>
  );
}

export default function SMSLog() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/sms-log").then((r) => setData(r.data)).catch(() => setData({ messages: [], counts: {}, configured: false }));
  useEffect(() => { load(); }, []);

  if (data === null) return <InlineLoader label="Loading SMS log" />;

  const msgs = data.messages || [];
  const c = data.counts || {};

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">SMS Log</h1>
          <p className="mt-1 text-sm text-ff-medium">Text-to-Give inbound messages, auto-replies, and outbound receipts.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="sms-log-refresh" className="shrink-0">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {!data.configured ? (
        <div className="mb-6 rounded-xl border border-ff-amber/30 bg-[#FFF7ED] p-4 text-sm text-ff-dark" data-testid="sms-not-configured">
          <b>SMS isn't live yet.</b> Add your Twilio toll-free number (and Verify Service SID) to the environment and point the number's inbound webhook at <code className="rounded bg-white px-1 py-0.5 text-xs">/api/sms/inbound</code>. Messages will appear here automatically once it's active.
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-ff-border bg-white p-4 text-sm text-ff-dark" data-testid="sms-configured">
          SMS is <span className="font-bold text-ff-green">live</span> on <span className="font-bold text-ff-blue">{data.number}</span>.
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Inbound" value={c.inbound || 0} testid="sms-stat-inbound" />
        <Stat label="Outbound" value={c.outbound || 0} testid="sms-stat-outbound" />
        <Stat label="Opt-outs" value={c.opt_outs || 0} testid="sms-stat-optouts" />
        <Stat label="Total" value={c.total || 0} testid="sms-stat-total" />
      </div>

      {msgs.length === 0 ? (
        <EmptyState icon={Inbox} title="No SMS activity yet" message="Inbound texts and outbound messages will show up here once your number is live." testid="sms-log-empty" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-ff-border bg-white shadow-sm" data-testid="admin-sms-log-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F4F6]">
                <TableHead>Direction</TableHead>
                <TableHead>From / To</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {msgs.map((m) => (
                <TableRow key={m.id} data-testid={`sms-row-${m.id}`}>
                  <TableCell>
                    {m.direction === "inbound" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#EFF6FF] px-2 py-0.5 text-xs font-bold text-ff-blue">
                        <ArrowDownLeft className="h-3 w-3" /> Inbound
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF3] px-2 py-0.5 text-xs font-bold text-ff-green">
                        <ArrowUpRight className="h-3 w-3" /> Outbound
                      </span>
                    )}
                    {m.status === "failed" && (
                      <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#FEF2F2] px-2 py-0.5 text-xs font-bold text-ff-red">
                        <Ban className="h-3 w-3" /> Failed
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-xs text-ff-dark">{m.from || m.to || "—"}</TableCell>
                  <TableCell className="max-w-md text-sm text-ff-dark">{m.body}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-ff-medium">{m.created_at ? new Date(m.created_at).toLocaleString() : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
